import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import LeaseUpload from '../components/LeaseUpload';
import { formatCurrency, formatDate } from '../utils/format';

// ── Skeleton for stat cards while a ledger is loading ────────────────────────

function StatCardSkeleton() {
  return (
    <div className="mt-4 grid grid-cols-3 gap-3 pt-4 border-t border-slate-100">
      {[0, 1, 2].map(i => (
        <div key={i} className="rounded-xl p-3 animate-pulse" style={{ background: '#F1F5F9' }}>
          <div className="h-3 rounded w-2/3 mb-2" style={{ background: '#CBD5E1' }} />
          <div className="h-4 rounded w-3/4 mb-1" style={{ background: '#CBD5E1' }} />
          <div className="h-3 rounded w-1/2" style={{ background: '#E2E8F0' }} />
        </div>
      ))}
    </div>
  );
}

// ── Skeleton for a full property card on initial page load ────────────────────

function PropertyCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 animate-pulse">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="h-5 rounded w-2/3 mb-2" style={{ background: '#E2E8F0' }} />
          <div className="h-4 rounded w-1/3 mb-1.5" style={{ background: '#F1F5F9' }} />
          <div className="h-3 rounded w-1/4" style={{ background: '#F1F5F9' }} />
        </div>
        <div className="h-9 w-24 rounded-lg" style={{ background: '#F1F5F9' }} />
      </div>
      <StatCardSkeleton />
    </div>
  );
}

// ── Colour helpers ────────────────────────────────────────────────────────────

function getPaidToCardStyle(ledger) {
  if (!ledger.inArrears) {
    return { background: '#DCFCE7', border: '1px solid #BBF7D0', labelColor: '#166534', valueColor: '#15803D', subColor: '#16A34A' };
  }
  if (ledger.arrearsDays <= 7) {
    return { background: '#FEF9C3', border: '1px solid #FDE68A', labelColor: '#92400E', valueColor: '#92400E', subColor: '#B45309' };
  }
  return { background: '#FEE2E2', border: '1px solid #FECACA', labelColor: '#991B1B', valueColor: '#991B1B', subColor: '#DC2626' };
}

function getPaidToSubtext(ledger) {
  if (!ledger.inArrears) {
    if (ledger.daysAhead >= 1) return `${ledger.daysAhead} day${ledger.daysAhead === 1 ? '' : 's'} ahead`;
    return 'Up to date';
  }
  return `${ledger.arrearsDays} day${ledger.arrearsDays === 1 ? '' : 's'} overdue`;
}

function getBalanceDisplay(ledger) {
  if (ledger.balanceOwing > 0.009) {
    return { text: `${formatCurrency(ledger.balanceOwing)} owing`, color: '#B45309' };
  }
  if (ledger.balanceOwing < -0.009) {
    return { text: `${formatCurrency(Math.abs(ledger.balanceOwing))} credit`, color: '#15803D' };
  }
  return { text: 'All clear ✓', color: '#15803D' };
}

// ── Stat cards row ────────────────────────────────────────────────────────────

function LedgerStatCards({ ledger }) {
  const paidStyle = getPaidToCardStyle(ledger);
  const paidSubtext = getPaidToSubtext(ledger);
  const balance = getBalanceDisplay(ledger);

  return (
    <div className="mt-4 grid grid-cols-3 gap-3 pt-4 border-t border-slate-100">
      {/* Paid to date */}
      <div className="rounded-xl p-3" style={{ background: paidStyle.background, border: paidStyle.border }}>
        <p className="text-xs font-medium" style={{ color: paidStyle.labelColor, opacity: 0.75 }}>Paid to</p>
        <p className="text-sm font-bold mt-0.5" style={{ color: paidStyle.valueColor }}>
          {formatDate(ledger.paidToDate)}
        </p>
        <p className="text-xs mt-0.5 font-medium" style={{ color: paidStyle.subColor }}>
          {paidSubtext}
        </p>
      </div>

      {/* Next due */}
      <div className="rounded-xl p-3" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
        <p className="text-xs font-medium text-slate-500">Next due</p>
        <p className="text-sm font-bold text-slate-800 mt-0.5">{formatDate(ledger.upcomingDueDate)}</p>
        <p className="text-xs text-slate-400 mt-0.5 font-mono">{formatCurrency(ledger.nextDueAmount)}</p>
      </div>

      {/* Balance */}
      <div className="rounded-xl p-3" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
        <p className="text-xs font-medium text-slate-500">Balance</p>
        <p className="text-sm font-bold mt-0.5 font-mono" style={{ color: balance.color }}>
          {balance.text}
        </p>
        <p className="text-xs text-slate-400 mt-0.5">
          {formatCurrency(ledger.totalPaid)} total paid
        </p>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { user } = useAuth();
  const [properties, setProperties]         = useState([]);
  const [ledgers, setLedgers]               = useState({});
  const [loadingLedgers, setLoadingLedgers] = useState(new Set());
  const [loading, setLoading]               = useState(true);
  const [showAddForm, setShowAddForm]       = useState(false);
  const [form, setForm] = useState({
    address: '', tenantName: '', tenantEmail: '',
    fortnightlyRent: '', leaseStartDate: '',
  });
  const [addError, setAddError]     = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);

  // Send reminder modal state (ADD 3)
  const [reminderModal, setReminderModal]     = useState(null); // { propertyId, tenantName, phone }
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderResult, setReminderResult]   = useState(null); // 'sent' | error string

  const openReminderModal = (property, ledger) => {
    setReminderResult(null);
    setReminderModal({
      propertyId: property.id,
      tenantName: property.tenant_name,
      phone: ledger?.tenantPhone || null,
    });
  };

  const handleSendReminder = async () => {
    if (!reminderModal) return;
    setReminderLoading(true);
    setReminderResult(null);
    try {
      await API.post(`/properties/${reminderModal.propertyId}/send-reminder`);
      setReminderResult('sent');
    } catch (err) {
      setReminderResult(err.response?.data?.error || 'Failed to send reminder');
    } finally {
      setReminderLoading(false);
    }
  };

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const { data } = await API.get('/properties');
      setProperties(data);
      for (const p of data) {
        if (p.tenancy_id) loadLedger(p.id);
      }
    } catch {
      // handle silently
    } finally {
      setLoading(false);
    }
  };

  const loadLedger = async (propertyId) => {
    setLoadingLedgers(prev => new Set(prev).add(propertyId));
    try {
      const { data } = await API.get(`/properties/${propertyId}/ledger`);
      // Merge tenantPhone into ledger so reminder modal can access it
      setLedgers(prev => ({ ...prev, [propertyId]: { ...data.ledger, tenantPhone: data.tenancy?.tenant_phone || null } }));
    } catch {
      setLedgers(prev => ({ ...prev, [propertyId]: null }));
    } finally {
      setLoadingLedgers(prev => {
        const next = new Set(prev);
        next.delete(propertyId);
        return next;
      });
    }
  };

  const handleAddProperty = async (e) => {
    e.preventDefault();
    setAddError('');
    setAddLoading(true);
    try {
      const { data } = await API.post('/properties', {
        ...form,
        fortnightlyRent: parseFloat(form.fortnightlyRent),
      });
      setProperties(prev => [data, ...prev]);
      const link = `${window.location.origin}/tenant/invite/${data.invite_token}`;
      setInviteLink({ link, tenantName: data.tenant_name });
      setShowAddForm(false);
      setForm({ address: '', tenantName: '', tenantEmail: '', fortnightlyRent: '', leaseStartDate: '' });
      if (data.tenancy_id) loadLedger(data.id);
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to add property');
    } finally {
      setAddLoading(false);
    }
  };

  // ── Initial page skeleton ──
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-between mb-6">
          <div className="animate-pulse">
            <div className="h-7 rounded w-40 mb-2" style={{ background: '#E2E8F0' }} />
            <div className="h-4 rounded w-28" style={{ background: '#F1F5F9' }} />
          </div>
          <div className="h-9 w-32 rounded-lg animate-pulse" style={{ background: '#E2E8F0' }} />
        </div>
        <div className="space-y-4">
          <PropertyCardSkeleton />
          <PropertyCardSkeleton />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Your properties</h1>
          <p className="text-sm text-slate-500 mt-0.5">Welcome back, {user?.name}</p>
        </div>
        <button
          onClick={() => { setShowAddForm(true); setInviteLink(null); }}
          className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 min-h-[44px]"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add property
        </button>
      </div>

      {/* ── Invite link banner ── */}
      {inviteLink && (
        <div className="mb-6 p-4 bg-brand-50 border border-brand-200 rounded-xl">
          <p className="text-sm font-semibold text-brand-800 mb-1">
            Property added! Send {inviteLink.tenantName} this link to set up their tenant login:
          </p>
          <div className="flex items-center gap-2 mt-2">
            <code className="flex-1 text-xs bg-white border border-brand-200 rounded-lg px-3 py-2 text-slate-700 truncate">
              {inviteLink.link}
            </code>
            <button
              onClick={() => navigator.clipboard.writeText(inviteLink.link)}
              className="text-xs bg-brand-600 text-white px-3 py-2 rounded-lg font-medium hover:bg-brand-700 transition-colors whitespace-nowrap min-h-[36px]"
            >
              Copy link
            </button>
          </div>
          <button onClick={() => setInviteLink(null)} className="text-xs text-brand-600 hover:underline mt-2 block">
            Dismiss
          </button>
        </div>
      )}

      {/* ── Add property form ── */}
      {showAddForm && (
        <div className="mb-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-800">Add a new property</h2>
            <button onClick={() => setShowAddForm(false)} className="text-slate-400 hover:text-slate-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <LeaseUpload onExtracted={(data) => {
            setForm({
              address:         data.propertyAddress || form.address,
              tenantName:      data.tenantName      || form.tenantName,
              tenantEmail:     data.tenantEmail     || form.tenantEmail,
              fortnightlyRent: data.fortnightlyRent ? String(data.fortnightlyRent) : form.fortnightlyRent,
              leaseStartDate:  data.leaseStartDate  || form.leaseStartDate,
            });
          }} />

          {addError && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
              {addError}
            </div>
          )}

          <form onSubmit={handleAddProperty} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Property address</label>
              <input
                value={form.address}
                onChange={e => setForm({ ...form, address: e.target.value })}
                required
                placeholder="123 Example St, Melbourne VIC 3000"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Tenant name</label>
              <input
                value={form.tenantName}
                onChange={e => setForm({ ...form, tenantName: e.target.value })}
                required
                placeholder="Alex Johnson"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Tenant email</label>
              <input
                type="email"
                value={form.tenantEmail}
                onChange={e => setForm({ ...form, tenantEmail: e.target.value })}
                required
                placeholder="alex@example.com"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Fortnightly rent (AUD)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={form.fortnightlyRent}
                  onChange={e => setForm({ ...form, fortnightlyRent: e.target.value })}
                  required
                  placeholder="1200.00"
                  className="w-full pl-8 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Lease start date</label>
              <input
                type="date"
                value={form.leaseStartDate}
                onChange={e => setForm({ ...form, leaseStartDate: e.target.value })}
                required
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowAddForm(false)}
                className="px-5 py-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors min-h-[44px]">
                Cancel
              </button>
              <button type="submit" disabled={addLoading}
                className="px-5 py-2.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white rounded-lg text-sm font-semibold transition-colors min-h-[44px]">
                {addLoading ? 'Adding...' : 'Add property'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Properties list / empty state ── */}
      {properties.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300">
          <div className="w-14 h-14 bg-brand-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <p className="font-semibold text-slate-800 text-base">Welcome to RentFriendly</p>
          <p className="text-sm text-slate-500 mt-1 mb-4">Let's set up your first property</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors inline-flex items-center gap-1.5 min-h-[44px]"
          >
            Add property
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      ) : (() => {
        // Compute summary stats from loaded ledgers
        const loadedLedgers    = Object.values(ledgers).filter(Boolean);
        const arrearsProps     = loadedLedgers.filter(l => l.inArrears);
        const monthTotal       = loadedLedgers.reduce((s, l) => s + (l.monthReceived ?? 0), 0);
        const currentMonth     = new Date().toLocaleString('en-AU', { month: 'long', year: 'numeric' });

        // Sort: arrears first, then by address
        const sorted = [...properties].sort((a, b) => {
          const la = ledgers[a.id];
          const lb = ledgers[b.id];
          const aArr = la?.inArrears ? 1 : 0;
          const bArr = lb?.inArrears ? 1 : 0;
          if (bArr !== aArr) return bArr - aArr;
          return a.address.localeCompare(b.address);
        });

        return (
          <>
            {/* ── Summary bar ── */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Properties</p>
                <p className="text-2xl font-bold text-slate-800 mt-0.5">{properties.length}</p>
              </div>
              <div className={`rounded-2xl border shadow-sm px-4 py-3 ${arrearsProps.length > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
                <p className={`text-xs font-semibold uppercase tracking-wide ${arrearsProps.length > 0 ? 'text-red-400' : 'text-slate-400'}`}>In arrears</p>
                <p className={`text-2xl font-bold mt-0.5 ${arrearsProps.length > 0 ? 'text-red-700' : 'text-slate-800'}`}>
                  {arrearsProps.length}
                </p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">This month</p>
                <p className="text-2xl font-bold text-slate-800 mt-0.5 font-mono">{formatCurrency(monthTotal)}</p>
                <p className="text-xs text-slate-400 mt-0.5">{currentMonth}</p>
              </div>
            </div>

            {/* ── Arrears alert — FIX 5 ── */}
            {arrearsProps.length > 0 && (
              <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-red-800">
                      {formatCurrency(arrearsProps.reduce((s, l) => s + l.arrearsAmount, 0))} outstanding across {arrearsProps.length} {arrearsProps.length === 1 ? 'property' : 'properties'}
                    </p>
                    <p className="text-xs text-red-600 mt-0.5">
                      Tenant{arrearsProps.length !== 1 ? 's' : ''} {arrearsProps.length === 1 ? 'is' : 'are'} in arrears — review below
                    </p>
                  </div>
                </div>
                <a href="#properties" className="text-xs font-semibold text-red-700 hover:text-red-800 whitespace-nowrap flex items-center gap-1">
                  View details
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            )}

            {/* ── Property cards — FIX 4 ── */}
            <div className="space-y-4" id="properties">
              {sorted.map(property => {
                const ledger         = ledgers[property.id];
                const ledgerLoading  = loadingLedgers.has(property.id);
                const hasActiveLease = !!property.tenancy_id;
                const inArrears      = !!ledger?.inArrears;
                const hasPhone       = !!ledger?.tenantPhone;

                return (
                  <div key={property.id}
                    className={`bg-white rounded-2xl border shadow-sm p-5 sm:p-6 transition-all ${inArrears ? 'border-red-200' : 'border-slate-200'}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h2 className="font-semibold text-slate-800 text-base truncate">{property.address}</h2>
                          {ledger && (
                            <StatusBadge
                              inArrears={ledger.inArrears}
                              arrearsDays={ledger.arrearsDays}
                              arrearsAmount={ledger.arrearsAmount}
                            />
                          )}
                        </div>
                        {hasActiveLease ? (
                          <>
                            <p className="text-sm text-slate-500 mt-0.5">
                              {property.tenant_name} · <span className="font-mono">{formatCurrency(property.fortnightly_rent)}</span> per fortnight
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              Lease started {formatDate(property.lease_start_date)}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-amber-600 mt-0.5 font-medium">No active tenancy</p>
                        )}
                      </div>

                      {/* Action buttons — FIX 4 */}
                      <div className="flex items-center gap-2 flex-wrap self-start sm:self-center">
                        {inArrears && (
                          <button
                            onClick={() => openReminderModal(property, ledger)}
                            className="text-sm px-3 py-2 rounded-lg font-medium border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 transition-colors whitespace-nowrap min-h-[36px] flex items-center gap-1.5">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            Remind
                          </button>
                        )}
                        <Link
                          to={`/properties/${property.id}`}
                          className={`text-sm px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap min-h-[36px] flex items-center ${
                            inArrears
                              ? 'bg-red-600 hover:bg-red-700 text-white'
                              : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                          }`}
                        >
                          View ledger
                        </Link>
                      </div>
                    </div>

                    {/* Stat row */}
                    {hasActiveLease && ledgerLoading && <StatCardSkeleton />}
                    {hasActiveLease && ledger && <LedgerStatCards ledger={ledger} />}
                  </div>
                );
              })}
            </div>

            {/* ── Send Reminder Modal — ADD 3 ── */}
            {reminderModal && (
              <>
                <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setReminderModal(null)} />
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm pointer-events-auto">
                    <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                      <h2 className="text-base font-semibold text-slate-800">Send SMS reminder</h2>
                      <button onClick={() => setReminderModal(null)} className="text-slate-400 hover:text-slate-600 p-1 rounded">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="p-5">
                      {reminderResult === 'sent' ? (
                        <div className="text-center py-4">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <svg className="w-6 h-6 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="text-sm font-semibold text-slate-800">Reminder sent!</p>
                          <p className="text-xs text-slate-500 mt-1">SMS delivered to {reminderModal.tenantName}</p>
                          <button onClick={() => setReminderModal(null)}
                            className="mt-4 w-full px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-medium transition-colors">
                            Done
                          </button>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-slate-600 mb-4">
                            Send an arrears SMS reminder to <strong>{reminderModal.tenantName}</strong>.
                          </p>
                          {!reminderModal.phone && (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                              No phone number on file. Add one via the Tenant tab on the ledger page first.
                            </div>
                          )}
                          {reminderResult && reminderResult !== 'sent' && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                              {reminderResult}
                            </div>
                          )}
                          <div className="flex gap-3">
                            <button onClick={() => setReminderModal(null)}
                              className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors min-h-[44px]">
                              Cancel
                            </button>
                            <button
                              onClick={handleSendReminder}
                              disabled={reminderLoading || !reminderModal.phone}
                              className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors min-h-[44px]">
                              {reminderLoading ? 'Sending...' : 'Send reminder'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        );
      })()}
    </Layout>
  );
}
